import type { Maybe } from '@seek/ca-myprofile-utils';
import { createForm, type RenderParams } from '@seek/forms-ui';
import { YearsOfExperience as YearsOfExperienceDialogContent } from '@seek/profile-edit-ui';
import { rules } from '@seek/validators-js';
import { useTranslations } from '@vocab/react';
import {
  Actions,
  Button,
  Stack,
  Dialog,
  HiddenVisually,
  Alert,
  Text,
} from 'braid-design-system';
import React, { useCallback, useEffect, useRef } from 'react';

import { useAppConfig } from 'src/modules/appConfig';
import { YEARS_OF_EXPERIENCE_FORM } from 'src/reducers/forms';

import translations from './.vocab';

export interface YearsOfExperience {
  option: Maybe<{ id: string }>;
  newToWorkforce: boolean;
}

interface FormValues {
  yearsOfExperience: YearsOfExperience;
}

interface ComponentProps {
  initialValues?: YearsOfExperience;
  displayDialog: boolean;
  closeYearsOfExperienceDialog: () => void;
  onFormDirtyChange: (dirty: boolean) => void;
  submitForm: (params: {
    dirty: boolean;
    formValues: YearsOfExperience;
  }) => void;
  onCancel: () => void;
  onSubmitFail?: (errors: string[]) => void;
  isLoading: boolean;
  hasError: boolean;
  hasYearsOfExperience: boolean;
}

const mapFormStateToProps = ({
  form: { dirty },
  values: { ...yearsOfExperience },
}: {
  form: { dirty: boolean };
  values: FormValues;
}) => ({
  dirty,
  ...yearsOfExperience,
});

type MappedProps = ReturnType<typeof mapFormStateToProps>;
type FormProps = RenderParams<FormValues> & MappedProps;
type YearsOfExperienceFormProps = ComponentProps & FormProps;

const { FormProvider, useField } = createForm<FormValues, MappedProps>(
  YEARS_OF_EXPERIENCE_FORM,
  {
    formType: 'braid',
  },
);

const Form = ({
  submitForm,
  onCancel,
  dirty,
  handleSubmit,
  displayDialog,
  onFormDirtyChange,
  closeYearsOfExperienceDialog,
  isLoading,
  hasError,
  hasYearsOfExperience,
}: YearsOfExperienceFormProps) => {
  const { t } = useTranslations(translations);
  const initialRender = useRef(true);

  const onSubmitFn = useCallback(
    () =>
      handleSubmit(({ yearsOfExperience: formValues }) => {
        submitForm({ dirty, formValues });
      }),
    [handleSubmit, submitForm, dirty],
  );

  useEffect(() => {
    if (onFormDirtyChange && !initialRender.current) {
      onFormDirtyChange(dirty);
    }
  }, [dirty, onFormDirtyChange]);

  useEffect(() => {
    if (initialRender.current === true) {
      initialRender.current = false;
    }
  }, []);

  const yearsOfExperienceField = useField({
    id: 'yearsOfExperience',
    ...rules.yearsOfExperience(),
  });

  return (
    <form>
      <Stack space="gutter">
        <Dialog
          id="yearsOfExperienceDialog"
          title={t('Update total years of experience')}
          open={displayDialog}
          onClose={closeYearsOfExperienceDialog}
          closeLabel={t('Close')}
        >
          <YearsOfExperienceDialogContent
            id="yearsOfExperience"
            hideNewToWorkforce={hasYearsOfExperience}
            {...yearsOfExperienceField}
          />
          {hasError ? (
            <Alert tone="critical">
              <Text>{t('An error occurred, your changes were not saved')}</Text>
            </Alert>
          ) : null}
          <Actions>
            <Button
              loading={isLoading}
              variant="solid"
              tone="formAccent"
              type="button"
              onClick={onSubmitFn()}
              data={{ automation: 'years-of-experience-save-button' }}
            >
              {isLoading ? t('Saving') : t('Save')}
            </Button>
            <Button
              onClick={onCancel}
              data={{
                automation: 'years-of-experience-cancel-button',
              }}
              tone="formAccent"
              variant="transparent"
            >
              {t('Cancel edit years of experience', {
                HiddenVisually: (v) => <HiddenVisually>{v}</HiddenVisually>,
              })}
            </Button>
          </Actions>
        </Dialog>
      </Stack>
    </form>
  );
};

const initialValues: FormValues = {
  yearsOfExperience: {
    option: null,
    newToWorkforce: false,
  },
};

const getInitialState = (yearsOfExperience?: YearsOfExperience): FormValues => {
  if (!yearsOfExperience) {
    return initialValues;
  }

  return {
    yearsOfExperience,
  };
};

export const YearsOfExperienceForm = (props: ComponentProps) => {
  const { language } = useAppConfig();
  return (
    <FormProvider
      mapFormStateToProps={mapFormStateToProps}
      initialValues={getInitialState(props.initialValues)}
      onSubmitFail={props.onSubmitFail ?? props.onSubmitFail}
      language={language}
    >
      {(formProps) => <Form {...formProps} {...props} />}
    </FormProvider>
  );
};
